import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { notify } from '../utils/notify';
import '../styles/bpo.css';
import '../styles/conciliacao-vendizap.css';

// Conciliação Vendizap (BPO Financeiro, 29/10/2026): títulos em aberto da conta Stripe no Omie
// × vendas do espelho do Stripe (elo cliente via stripe_clientes + valor + data). Baixa no Omie
// um por vez (modal) ou todos os casados em lote (blocos de 20, um resultado por linha).
// Spec: docs/superpowers/specs/2026-10-29-conciliacao-vendizap-design.md
// Etapa 2 (30/10/2026): linha expansível com o detalhe título × venda e as abas "Stripe sem Omie"
// (vendas sem título no Omie) e "Repasses" (payouts × transferência na conta Stripe do Omie).

// Etapa 4 (30/10/2026): a mesma página atende o Asaas (recebimentos Pix × conta ASAAS do Omie).
// Spec: docs/superpowers/plans/2026-10-30-conciliacao-asaas-vendizap.md (Task 2).
const API = process.env.REACT_APP_API_BASE;
const PROVEDORES = {
  stripe: {
    url: `${API}/api/bpofin/conciliacao-vendizap/`,
    titulo: 'Conciliação Stripe Vend.',
    nome: 'Stripe',
    conta: 'Stripe',
    rotuloVenda: 'Venda no Stripe',
    abas: { em_aberto: 'Em aberto no Omie', sobras: 'Stripe sem Omie', repasses: 'Repasses', extrato: 'Extrato' },
    abaSobrasChave: 'stripe_sem_omie',
    abaRepassesChave: 'repasses',
    subtitulos: {
      em_aberto: 'Títulos em aberto da conta Stripe no Omie × vendas do Stripe',
      sobras: 'Vendas do Stripe sem título correspondente no Omie',
      repasses: 'Repasses do Stripe ao banco × transferências na conta Stripe do Omie',
      extrato: 'Extrato do Stripe linha a linha × conta Stripe no Omie',
    },
    icone: 'fa-money-bill-transfer',
    iconeVenda: 'fab fa-stripe-s',
    corVenda: '#635bff',
    temRepasseComposto: true,
    temStatusPendente: true,
    temLancarRepasse: true,
    eloCliente: 'userId',
    textoBaixa: 'na data da venda',
    textoBaixaLote: 'na conta Stripe, na data da venda',
    buscaPlaceholder: 'Cliente, OS, RPS, NF, valor ou cobrança Stripe',
    vazioSobras: 'Nenhuma venda do Stripe sem título no Omie neste período.',
    vazioRepasses: 'Nenhum repasse neste período.',
    semDepara: 'Cliente sem de-para (userId não encontrado no Omie)',
    ajudaSobras: 'Recente = a nota ainda pode ser emitida (7 dias). Sem título = venda sem nota no Omie: verificar com o cliente.',
    ajudaRepasses: 'Sem transferência = o repasse chegou no banco, mas não há saída correspondente na conta Stripe do Omie.',
  },
  asaas: {
    url: `${API}/api/bpofin/conciliacao-vendizap-asaas/`,
    titulo: 'Conciliação Asaas Vend.',
    nome: 'Asaas',
    conta: 'ASAAS',
    rotuloVenda: 'Recebimento no Asaas',
    abas: { em_aberto: 'Em aberto no Omie', sobras: 'Asaas sem Omie', repasses: 'Saques', extrato: 'Extrato' },
    abaSobrasChave: 'sem_omie',
    abaRepassesChave: 'saques',
    subtitulos: {
      em_aberto: 'Títulos em aberto da conta ASAAS no Omie × recebimentos Pix do Asaas',
      sobras: 'Recebimentos do Asaas sem título correspondente no Omie',
      repasses: 'Saques do Asaas × transferências na conta ASAAS do Omie',
      extrato: 'Extrato do Asaas linha a linha × conta ASAAS no Omie',
    },
    icone: 'fa-qrcode',
    iconeVenda: 'fas fa-qrcode',
    corVenda: '#0030b9',
    temRepasseComposto: false,
    temStatusPendente: false,
    temLancarRepasse: false,
    eloCliente: 'email',
    textoBaixa: 'na data do recebimento',
    textoBaixaLote: 'na conta ASAAS, na data do recebimento',
    buscaPlaceholder: 'Cliente, OS, RPS, NF, valor ou cobrança Asaas',
    vazioSobras: 'Nenhum recebimento do Asaas sem título no Omie neste período.',
    vazioRepasses: 'Nenhum saque neste período.',
    semDepara: 'Cliente sem de-para (e-mail e CPF/CNPJ não encontrados no Omie)',
    ajudaSobras: 'Recente = a nota ainda pode ser emitida (7 dias). Sem título = recebimento do Asaas sem nota no Omie: verificar com o cliente.',
    ajudaRepasses: 'Sem transferência = o saque saiu do Asaas, mas não há saída correspondente na conta ASAAS do Omie.',
  },
};
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v));
const dataBr = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '-');
// Datas sem hora (Asaas: 'YYYY-MM-DD') mostram só a data.
const dataHoraBr = (s) => (s ? (s.length <= 10 ? dataBr(s) : `${dataBr(s)} ${s.slice(11, 16)}`) : '-');
const msgErro = (e) => e?.response?.data?.mensagem || e?.response?.data?.erro || e?.message || 'Falha inesperada';
const LOTE = 20;
const LOTE_REP = 10;                                   // repasses por chamada (limite do backend)
// Etapa 3 (30/10/2026): lançar no Omie a transferência (e a tarifa) dos repasses sem saída na conta
// Stripe, um por vez ou em lote; e linha expansível na aba "Stripe sem Omie" com os títulos do cliente.
const CONTAS_OMIE = { '8565353257': 'Stripe', '8818985471': 'ASAAS' };
// Só repasse concluído (pago) pode ser lançado no Omie.
const repasseConcluido = (r) => ['paid', 'available', ''].includes(String(r.status_repasse || r.status || '').toLowerCase());
const nomeConta = (c) => (c == null || c === '' ? '—' : (CONTAS_OMIE[String(c)] || String(c)));

const SITUACOES = {
  casado: { rotulo: 'Casado', classe: 'bg-success-subtle text-success-emphasis border-success-subtle', icone: 'fa-link' },
  varias: { rotulo: 'Várias vendas', classe: 'bg-warning-subtle text-warning-emphasis border-warning-subtle', icone: 'fa-code-branch' },
  estornada: { rotulo: 'Estornada', classe: 'bg-danger-subtle text-danger-emphasis border-danger-subtle', icone: 'fa-rotate-left' },
  sem_venda: { rotulo: 'Sem venda', classe: 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle', icone: 'fa-link-slash' },
  sem_depara: { rotulo: 'Sem de-para', classe: 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle', icone: 'fa-user-slash' },
};
const CARDS = [
  { chave: '', titulo: 'Em aberto', campo: 'em_aberto', cor: '#00205b', icone: 'fa-file-invoice' },
  { chave: 'casado', titulo: 'Casados', campo: 'casados', cor: '#198754', icone: 'fa-link' },
  { chave: 'varias', titulo: 'Várias vendas', campo: 'varias', cor: '#b8860b', icone: 'fa-code-branch' },
  { chave: 'sem_venda', titulo: 'Sem venda', campo: 'sem_venda', cor: '#6c757d', icone: 'fa-link-slash' },
  { chave: 'baixados', titulo: 'Baixados hoje', campo: 'baixados_hoje', cor: '#00b5cc', icone: 'fa-circle-check' },
];

// Chaves internas fixas; o rótulo vem de P.abas e o parâmetro da API de P.abaSobrasChave/P.abaRepassesChave.
const ABAS = [
  { chave: 'em_aberto', rotuloDe: 'em_aberto', icone: 'fa-file-invoice' },
  { chave: 'stripe_sem_omie', rotuloDe: 'sobras', icone: 'fa-magnifying-glass-dollar' },
  { chave: 'repasses', rotuloDe: 'repasses', icone: 'fa-building-columns' },
  { chave: 'extrato', rotuloDe: 'extrato', icone: 'fa-list-ul' },
];
const PERIODOS = [15, 30, 45, 90, 180];
// Aba Extrato (30/10/2026): cada linha do extrato do provedor × Omie, em período de/até.
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const periodoExtratoPadrao = () => {
  const hoje = new Date();
  const de = new Date(hoje); de.setDate(de.getDate() - 15);
  return { de: isoLocal(de), ate: isoLocal(hoje) };
};
const FILTROS_EXTRATO = [
  { chave: 'todos', rotulo: 'Todas as linhas', teste: () => true },
  { chave: 'nao', rotulo: 'Não conciliados', teste: (l) => !l.omie },
  { chave: 'conciliados', rotulo: 'Conciliados', teste: (l) => !!l.omie },
  { chave: 'baixaveis', rotulo: 'Prontos para conciliar', teste: (l) => l.acao === 'baixar' },
  { chave: 'vendas', rotulo: 'Vendas', teste: (l) => l.tipo === 'venda' },
  { chave: 'tarifas', rotulo: 'Tarifas', teste: (l) => l.tipo === 'tarifa' },
];
const LIMITE_EXTRATO = 400;                            // linhas renderizadas na tabela do extrato
const MOTIVOS_OMIE_SEM_GATEWAY = {
  sem_venda_no_gateway: { rotulo: (nome) => `Recebido no Omie sem venda no ${nome}`, classe: 'bg-danger-subtle text-danger-emphasis border-danger-subtle' },
  sem_correspondencia: { rotulo: () => 'Sem correspondência', classe: 'bg-warning-subtle text-warning-emphasis border-warning-subtle' },
};
const MOTIVOS = {
  sem_titulo: { rotulo: 'Sem título no Omie', classe: 'bg-danger-subtle text-danger-emphasis border-danger-subtle' },
  recente: { rotulo: 'Recente (nota pode sair)', classe: 'bg-warning-subtle text-warning-emphasis border-warning-subtle' },
  estornada: { rotulo: 'Estornada', classe: 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle' },
  sem_depara: { rotulo: 'Sem de-para', classe: 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle' },
};
// chave null = card só informativo (não filtra); '' = todas as sobras (limpa o filtro).
const CARDS_SOBRAS = [
  { chave: null, titulo: 'Vendas no período', campo: 'vendas_periodo', cor: '#00205b', icone: 'fa-cart-shopping' },
  { chave: null, titulo: 'Casadas', campo: 'casadas', cor: '#198754', icone: 'fa-link' },
  { chave: '', titulo: 'Sobras', campo: 'sobras', cor: '#b8860b', icone: 'fa-circle-exclamation', valor: 'valor_sobras' },
  { chave: 'sem_titulo', titulo: 'Sem título', campo: 'sem_titulo', cor: '#dc3545', icone: 'fa-file-circle-xmark' },
  { chave: 'recente', titulo: 'Recentes', campo: 'recentes', cor: '#e0a800', icone: 'fa-clock' },
  { chave: 'estornada', titulo: 'Estornadas', campo: 'estornadas', cor: '#6c757d', icone: 'fa-rotate-left' },
  { chave: 'sem_depara', titulo: 'Sem de-para', campo: 'sem_depara', cor: '#6c757d', icone: 'fa-user-slash' },
];
const CARDS_REP = [
  { titulo: 'Repasses', campo: 'repasses', cor: '#00205b', icone: 'fa-building-columns' },
  { titulo: 'Valor repassado', campo: 'valor', cor: '#00b5cc', icone: 'fa-money-bill-transfer', moeda: true },
  { titulo: 'Com transferência no Omie', campo: 'com_omie', cor: '#198754', icone: 'fa-link' },
  { titulo: 'Sem transferência', campo: 'sem_omie', cor: '#dc3545', icone: 'fa-link-slash' },
  { titulo: 'Tarifas', campo: 'tarifas', cor: '#6c757d', icone: 'fa-percent', moeda: true },
];
// Asaas: saques não têm vendas/tarifas por saque; "tarifas" do resumo = taxas do período.
const CARDS_SAQUES = [
  { titulo: 'Saques', campo: 'repasses', cor: '#00205b', icone: 'fa-building-columns' },
  { titulo: 'Valor sacado', campo: 'valor', cor: '#00b5cc', icone: 'fa-money-bill-transfer', moeda: true },
  { titulo: 'Com transferência no Omie', campo: 'com_omie', cor: '#198754', icone: 'fa-link' },
  { titulo: 'Sem transferência', campo: 'sem_omie', cor: '#dc3545', icone: 'fa-link-slash' },
  { titulo: 'Taxas do período', campo: 'tarifas', cor: '#6c757d', icone: 'fa-percent', moeda: true },
];
const STATUS_REPASSE = { paid: 'Pago', in_transit: 'Em trânsito', pending: 'Pendente', failed: 'Falhou', canceled: 'Cancelado', DONE: 'Concluído' };
const CLASSE_STATUS_REPASSE = {
  DONE: 'bg-success-subtle text-success-emphasis border-success-subtle',
  paid: 'bg-success-subtle text-success-emphasis border-success-subtle',
  in_transit: 'bg-warning-subtle text-warning-emphasis border-warning-subtle',
  pending: 'bg-warning-subtle text-warning-emphasis border-warning-subtle',
  failed: 'bg-danger-subtle text-danger-emphasis border-danger-subtle',
  canceled: 'bg-danger-subtle text-danger-emphasis border-danger-subtle',
};
const meioVenda = (v) => [v.metodo, v.bandeira, v.parcelas ? `${v.parcelas}x` : null].filter(Boolean).join(' · ');
const badgeRecebido = <span className="badge bg-success-subtle text-success-emphasis">Recebido</span>;
const badgeStatusStripe = (status, P) => (P && !P.temStatusPendente ? badgeRecebido : (
  <span className={`badge ${status === 'available' ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`}>
    {status === 'available' ? 'Disponível' : 'Pendente'}
  </span>
));

// Par rótulo/valor do detalhe da linha expandida.
function Campo({ rotulo, children, mono }) {
  return (
    <div className="col-6 col-md-3">
      <small className="text-muted d-block">{rotulo}</small>
      <span className={mono ? 'font-monospace small' : undefined} style={{ wordBreak: 'break-all' }}>{children ?? '—'}</span>
    </div>
  );
}

// Modal no padrão do Saber (header branco, sem window.confirm).
function Modal({ titulo, children, rodape, onFechar, ocupado }) {
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1080 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
              <i className="fas fa-circle-check me-2" style={{ color: '#00b5cc' }}></i>{titulo}
            </h5>
            <button type="button" className="btn-close" onClick={onFechar} disabled={ocupado}></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer bg-light">{rodape}</div>
        </div>
      </div>
    </div>
  );
}

function VendaStripe({ v, P }) {
  if (!v) return <span className="text-muted">—</span>;
  const meio = meioVenda(v);
  return (
    <div style={{ lineHeight: 1.25 }}>
      <div><b>{dataHoraBr(v.criado)}</b> · {brl(v.valor)}
        {P.temStatusPendente ? (<>
        <span className={`badge ms-2 ${v.status === 'available' ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`}>
          {v.status === 'available' ? 'Disponível' : 'Pendente'}
        </span>
        <i className="fas fa-circle-info ms-1 text-muted" style={{ cursor: 'help', fontSize: 12 }} aria-label="O que significa"
          title={v.status === 'available'
            ? 'Disponível: o Stripe já liberou o dinheiro e ele entrou num repasse para o banco (po_...). Além da baixa do título, esse valor já saiu da conta Stripe.'
            : 'Pendente: o cliente pagou, mas o Stripe segura o dinheiro por ~30 dias. A venda prova o pagamento e o título pode ser baixado na conta Stripe do Omie; o dinheiro só vai ao banco no repasse.'}></i>
        </>) : <span className="badge ms-2 bg-success-subtle text-success-emphasis">Recebido</span>}
      </div>
      <small className="text-muted">{meio || '—'} · tarifa {brl(v.tarifa)} · líquido {brl(v.liquido)}{v.repasse ? ` · repasse ${v.repasse}` : ''}</small>
      <br /><small className="text-muted font-monospace">{v.cobranca || v.pi || v.txn}</small>
      {Number(v.estornado) > 0 && <div><small className="text-danger"><i className="fas fa-rotate-left me-1"></i>Estornado {brl(v.estornado)}</small></div>}
    </div>
  );
}

export default function BpoFinConciliacaoVendizap() {
  return <ConciliacaoVendizap provedor="stripe" />;
}

export function ConciliacaoVendizap({ provedor = 'stripe' }) {
  const P = PROVEDORES[provedor] || PROVEDORES.stripe;
  const URL = P.url;
  const [titulos, setTitulos] = useState([]);
  const [resumo, setResumo] = useState({});
  const [atualizadoEm, setAtualizadoEm] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [escolha, setEscolha] = useState({});          // codigo do título → txn escolhida (situação "varias")
  const [resultados, setResultados] = useState({});    // codigo do título → {ok, ja_baixado, codigo_baixa, erro}
  const [modal, setModal] = useState(null);            // {tipo: 'um', linha} | {tipo: 'lote'}
  const [ocupado, setOcupado] = useState(false);
  const [lote, setLote] = useState(null);              // {total, feitos, ok, jaBaixados, erros: [{os, nome, erro}]}
  const [expandido, setExpandido] = useState({});      // codigo do título → linha aberta
  // Etapa 2: abas "Stripe sem Omie" e "Repasses"
  const [aba, setAba] = useState('em_aberto');
  const [dias, setDias] = useState(45);
  const [sobras, setSobras] = useState([]);
  const [resumoSobras, setResumoSobras] = useState({});
  const [outros, setOutros] = useState([]);            // Asaas: taxas, comissões, estornos... × extrato Omie
  const [filtroOutros, setFiltroOutros] = useState('sem_omie');
  const [repasses, setRepasses] = useState([]);
  const [resumoRep, setResumoRep] = useState({});
  const [atualizadoAba, setAtualizadoAba] = useState('');
  const [carregandoAba, setCarregandoAba] = useState(false);
  const [filtroMotivo, setFiltroMotivo] = useState('');
  const reqRef = useRef(0);                            // id da última requisição das abas novas
  // Etapa 3: lançamento dos repasses no Omie e detalhe das sobras
  const [resultadosRep, setResultadosRep] = useState({}); // po → {ok, lanc_transf, lanc_tarifa, erro}
  const [modalRep, setModalRep] = useState(null);         // {tipo: 'um', r} | {tipo: 'lote'}
  const [loteRep, setLoteRep] = useState(null);           // {total, feitos, ok, erros: [{po, valor, erro}]}
  const [ocupadoRep, setOcupadoRep] = useState(false);
  const [expandidoSobra, setExpandidoSobra] = useState({}); // txn → linha aberta
  // Aba Extrato: linhas do extrato do provedor × Omie (de/até)
  const [extrato, setExtrato] = useState(null);
  const [periodoExtrato, setPeriodoExtrato] = useState(periodoExtratoPadrao);
  const [filtroExtrato, setFiltroExtrato] = useState('todos');
  const [buscaExtrato, setBuscaExtrato] = useState('');
  const [carregandoExtrato, setCarregandoExtrato] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const { data } = await axios.get(URL, { headers: headers() });
      setTitulos(data.titulos || []);
      setResumo(data.resumo || {});
      setAtualizadoEm(data.atualizado_em || '');
      setResultados({});
    } catch (e) {
      notify.erro(`Não foi possível carregar a conciliação: ${msgErro(e)}`);
    } finally {
      setCarregando(false);
    }
  };
  useEffect(() => { carregar(); }, []);

  const carregarExtrato = async (per = periodoExtrato) => {
    if (!per.de || !per.ate) { notify.erro('Informe as datas De e Até.'); return; }
    if (per.de > per.ate) { notify.erro('A data De não pode ser depois da data Até.'); return; }
    const id = ++reqRef.current;   // mesma trava das outras abas: só a última requisição vale
    setCarregandoExtrato(true);
    try {
      const { data } = await axios.get(URL, { headers: headers(), params: { aba: 'extrato', de: per.de, ate: per.ate } });
      if (id !== reqRef.current) return;
      setExtrato(data || null);
      setAtualizadoAba(data?.atualizado_em || '');
    } catch (e) {
      if (id === reqRef.current) notify.erro(msgErro(e));
    } finally {
      if (id === reqRef.current) setCarregandoExtrato(false);
    }
  };

  const carregarAba = async (qual, d) => {
    if (qual === 'em_aberto') { carregar(); return; }
    if (qual === 'extrato') { carregarExtrato(); return; }
    const id = ++reqRef.current;   // só a última requisição mexe no estado (troca rápida de aba/período)
    setCarregandoAba(true);
    try {
      const { data } = await axios.get(URL, { headers: headers(), params: { aba: qual === 'stripe_sem_omie' ? P.abaSobrasChave : P.abaRepassesChave, dias: d } });
      if (id !== reqRef.current) return;
      if (qual === 'stripe_sem_omie') {
        setSobras(data.vendas || []);
        setResumoSobras(data.resumo || {});
        setOutros(data.outros || []);
      } else {
        setRepasses(data.repasses || []);
        setResumoRep(data.resumo || {});
        setResultadosRep({});      // o banco passa a ser a fonte do que já foi lançado
      }
      setAtualizadoAba(data.atualizado_em || '');
    } catch (e) {
      if (id === reqRef.current) notify.erro(`Não foi possível carregar a aba: ${msgErro(e)}`);
    } finally {
      if (id === reqRef.current) setCarregandoAba(false);
    }
  };

  const trocarAba = (nova) => {
    if (nova === aba || ocupado || ocupadoRep) return;
    reqRef.current += 1;           // descarta a resposta pendente da aba anterior
    setCarregandoAba(false);
    setCarregandoExtrato(false);
    setAba(nova);
    setAtualizadoAba('');
    if (nova !== 'em_aberto') carregarAba(nova, dias);
  };

  const trocarDias = (d) => { setDias(d); carregarAba(aba, d); };

  const sobrasVisiveis = useMemo(() => sobras.filter((v) => !filtroMotivo || v.motivo === filtroMotivo), [sobras, filtroMotivo]);

  const alternar = (codigo) => setExpandido((p) => ({ ...p, [codigo]: !p[codigo] }));

  const linhasExtratoFiltradas = useMemo(() => {
    const linhas = extrato?.linhas || [];
    const teste = (FILTROS_EXTRATO.find((f) => f.chave === filtroExtrato) || FILTROS_EXTRATO[0]).teste;
    const q = buscaExtrato.trim().toLowerCase();
    return linhas.filter((l) => teste(l)
      && (!q || [l.descricao, l.nome, l.cobranca, l.rotulo].some((x) => String(x || '').toLowerCase().includes(q))));
  }, [extrato, filtroExtrato, buscaExtrato]);

  // Nome do cliente no provedor, consultado ao vivo ao expandir (só Asaas; nada é gravado no banco).
  const [nomesProvedor, setNomesProvedor] = useState({});
  const buscarNomeProvedor = (cus) => {
    if (P.eloCliente === 'userId' || !cus || nomesProvedor[cus] !== undefined) return;
    setNomesProvedor((p) => ({ ...p, [cus]: null }));
    axios.get(URL, { params: { aba: 'cliente', cus }, headers: headers() })
      .then(({ data }) => setNomesProvedor((p) => ({ ...p, [cus]: data.nome || (data.aviso ? `— (${data.aviso})` : '—') })))
      .catch(() => setNomesProvedor((p) => ({ ...p, [cus]: '—' })));
  };
  const nomeProvedor = (cus) => (nomesProvedor[cus] === undefined || nomesProvedor[cus] === null
    ? <span className="text-muted"><i className="fas fa-spinner fa-spin me-1"></i>buscando…</span>
    : nomesProvedor[cus]);

  const vendaDaLinha = (l) => (l.situacao === 'varias' && escolha[l.codigo]
    ? (l.candidatas.find((c) => c.txn === escolha[l.codigo]) || l.venda)
    : l.venda);

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return titulos.filter((l) => (!filtro || l.situacao === filtro)
      && (!q || [l.nome, l.cliente, l.os, l.documento, l.nf, String(l.valor), l.venda?.cobranca].some((x) => String(x || '').toLowerCase().includes(q))));
  }, [titulos, filtro, busca]);

  const casados = useMemo(() => titulos.filter((l) => l.situacao === 'casado' && !resultados[l.codigo]?.ok), [titulos, resultados]);
  const totalCasados = casados.reduce((s, l) => s + Number(l.valor || 0), 0);

  const aplicarResultado = (r) => {
    setResultados((p) => ({ ...p, [r.codigo_lancamento_omie]: r }));
    if (r.ok) {
      setResumo((p) => ({ ...p, baixados_hoje: (p.baixados_hoje || 0) + 1 }));
    }
  };

  const baixarUm = async (l) => {
    const venda = vendaDaLinha(l);
    setOcupado(true);
    try {
      const { data } = await axios.post(URL, { acao: 'baixar', codigo_lancamento_omie: l.codigo, txn_id: venda.txn }, { headers: headers() });
      aplicarResultado(data);
      if (!data.ok) { notify.erro(`OS ${l.os}: ${data.erro || 'O Omie recusou a baixa'}`); return; }
      if (data.aviso) notify.aviso(data.aviso);
      notify.sucesso(data.ja_baixado ? `OS ${l.os}: já estava baixada no Omie.` : `OS ${l.os} baixada no Omie (baixa ${data.codigo_baixa}).`);
      setModal(null);
      if (aba === 'extrato') carregarExtrato();
    } catch (e) {
      setResultados((p) => ({ ...p, [l.codigo]: { codigo_lancamento_omie: l.codigo, ok: false, erro: msgErro(e) } }));
      notify.erro(`OS ${l.os}: ${msgErro(e)}`);
    } finally {
      setOcupado(false);
    }
  };

  const baixarLote = async () => {
    const itens = casados.map((l) => ({ codigo_lancamento_omie: l.codigo, txn_id: l.venda.txn, os: l.os, nome: l.nome }));
    setOcupado(true);
    setLote({ total: itens.length, feitos: 0, ok: 0, jaBaixados: 0, erros: [] });
    for (let i = 0; i < itens.length; i += LOTE) {
      const bloco = itens.slice(i, i + LOTE);
      try {
        const { data } = await axios.post(URL, {
          acao: 'baixar_lote',
          itens: bloco.map(({ codigo_lancamento_omie, txn_id }) => ({ codigo_lancamento_omie, txn_id })),
        }, { headers: headers() });
        (data.resultados || []).forEach((r, j) => {
          aplicarResultado(r);
          setLote((p) => ({
            ...p, feitos: p.feitos + 1,
            ok: p.ok + (r.ok && !r.ja_baixado ? 1 : 0),
            jaBaixados: p.jaBaixados + (r.ja_baixado ? 1 : 0),
            erros: r.ok ? p.erros : [...p.erros, { os: bloco[j].os, nome: bloco[j].nome, erro: r.erro }],
          }));
        });
        const faltam = bloco.length - (data.resultados || []).length;
        if (faltam > 0) {
          setLote((p) => ({
            ...p, feitos: p.feitos + faltam,
            erros: [...p.erros, ...bloco.slice((data.resultados || []).length).map((b) => ({ os: b.os, nome: b.nome, erro: 'sem resposta do servidor para este item' }))],
          }));
        }
      } catch (e) {
        const erro = msgErro(e);
        bloco.forEach((b) => setResultados((p) => ({ ...p, [b.codigo_lancamento_omie]: { codigo_lancamento_omie: b.codigo_lancamento_omie, ok: false, erro } })));
        setLote((p) => ({ ...p, feitos: p.feitos + bloco.length, erros: [...p.erros, ...bloco.map((b) => ({ os: b.os, nome: b.nome, erro }))] }));
      }
    }
    setOcupado(false);
  };

  const fecharLote = () => { setModal(null); setLote(null); carregar(); };

  // ── Repasses: lançamento no Omie (transferência Stripe → Banco Inter + tarifa na conta Stripe) ──
  const pendentesRep = useMemo(
    // parciais ficam de fora: precisam do "Completar" individual
    () => repasses.filter((r) => r.po && !r.omie && r.lancamento !== 'ok' && r.lancamento !== 'parcial'
      && !resultadosRep[r.po]?.ok && resultadosRep[r.po]?.lanc_transf == null && repasseConcluido(r)),
    [repasses, resultadosRep],
  );
  const totalPendRep = pendentesRep.reduce((s, r) => s + Number(r.valor || 0), 0);
  const tarifasPendRep = pendentesRep.reduce((s, r) => s + Number(r.tarifas || 0), 0);

  const lancarRepasse = async (r) => {
    setOcupadoRep(true);
    try {
      const { data } = await axios.post(URL, { acao: 'lancar_repasse', po: r.po }, { headers: headers() });
      setResultadosRep((p) => ({ ...p, [r.po]: data }));
      if (!data.ok) { notify.erro(`Repasse ${r.po}: ${data.erro || 'O Omie recusou o lançamento'}`); return; }
      notify.sucesso(`Repasse ${r.po} lançado no Omie (transferência ${data.lanc_transf || 'já existia'}${data.lanc_tarifa ? `, tarifa ${data.lanc_tarifa}` : ''}).`);
      setModalRep(null);
    } catch (e) {
      setResultadosRep((p) => ({ ...p, [r.po]: { po: r.po, ok: false, erro: msgErro(e) } }));
      notify.erro(`Repasse ${r.po}: ${msgErro(e)}`);
    } finally {
      setOcupadoRep(false);
    }
  };

  const lancarRepassesLote = async () => {
    const itens = pendentesRep.map((r) => ({ po: r.po, valor: r.valor }));
    setOcupadoRep(true);
    setLoteRep({ total: itens.length, feitos: 0, ok: 0, erros: [] });
    for (let i = 0; i < itens.length; i += LOTE_REP) {
      const bloco = itens.slice(i, i + LOTE_REP);
      try {
        const { data } = await axios.post(URL, { acao: 'lancar_repasses_lote', pos: bloco.map((b) => b.po) }, { headers: headers() });
        const res = data.resultados || [];
        res.forEach((r, j) => {
          const po = r.po || bloco[j]?.po;
          setResultadosRep((p) => ({ ...p, [po]: r }));
          setLoteRep((p) => ({
            ...p, feitos: p.feitos + 1, ok: p.ok + (r.ok ? 1 : 0),
            erros: r.ok ? p.erros : [...p.erros, { po, valor: bloco[j]?.valor, erro: r.erro }],
          }));
        });
        const faltam = bloco.slice(res.length);
        if (faltam.length) {
          setLoteRep((p) => ({
            ...p, feitos: p.feitos + faltam.length,
            erros: [...p.erros, ...faltam.map((b) => ({ po: b.po, valor: b.valor, erro: 'sem resposta do servidor para este repasse' }))],
          }));
        }
      } catch (e) {
        const erro = msgErro(e);
        bloco.forEach((b) => setResultadosRep((p) => ({ ...p, [b.po]: { po: b.po, ok: false, erro } })));
        setLoteRep((p) => ({ ...p, feitos: p.feitos + bloco.length, erros: [...p.erros, ...bloco.map((b) => ({ po: b.po, valor: b.valor, erro }))] }));
      }
    }
    setOcupadoRep(false);
  };

  const fecharLoteRep = () => { setModalRep(null); setLoteRep(null); carregarAba('repasses', dias); };

  // Coluna "Ação" da aba Repasses.
  // Ordem: lançado → parcial (antes do "omie", que pode ser a nossa própria transferência) → omie → botão.
  const acaoRepasse = (r) => {
    if (!P.temLancarRepasse) return null;
    const res = resultadosRep[r.po];
    if (r.lancamento === 'ok' || res?.ok) {
      const lt = res?.ok ? res.lanc_transf : r.lanc_transf_omie;
      const lf = res?.ok ? res.lanc_tarifa : r.lanc_tarifa_omie;
      return (
        <span className="badge border bg-success-subtle text-success-emphasis border-success-subtle" title={r.lancado_em ? `Lançado em ${dataHoraBr(r.lancado_em)}` : undefined}>
          <i className="fas fa-check me-1"></i>Lançado · transf. {lt || 'já existia'}{lf ? ` · tarifa ${lf}` : ''}
        </span>
      );
    }
    const parcialSessao = res && !res.ok && res.lanc_transf != null;
    if (r.po && (r.lancamento === 'parcial' || parcialSessao)) {
      const lt = parcialSessao ? res.lanc_transf : r.lanc_transf_omie;
      return (
        <>
          <span className="badge border bg-warning-subtle text-warning-emphasis border-warning-subtle" title={res?.erro || r.erro_lancamento || undefined}>
            Transf. {lt || 'já existia'} ok · tarifa pendente
          </span>
          <button className="btn btn-sm btn-outline-primary ms-1" disabled={ocupadoRep} onClick={() => setModalRep({ tipo: 'um', r })}>
            <i className="fas fa-plus me-1"></i>Completar
          </button>
        </>
      );
    }
    if (r.omie || !r.po) return <span className="text-muted">—</span>;
    if (!repasseConcluido(r)) return <span className="text-muted" title="Só repasses pagos podem ser lançados">—</span>;
    const erro = res?.erro || r.erro_lancamento;
    return (
      <button className="btn btn-sm btn-outline-primary" disabled={ocupadoRep} title={erro || undefined}
        onClick={() => setModalRep({ tipo: 'um', r })}>
        <i className={`fas ${erro ? 'fa-triangle-exclamation' : 'fa-paper-plane'} me-1`}></i>{erro ? 'Tentar de novo' : 'Lançar no Omie'}
      </button>
    );
  };

  // Detalhe da linha expandida na aba "Stripe sem Omie": a venda e os títulos do cliente perto dela.
  const detalheSobra = (v) => {
    const tits = v.titulos_cliente || [];
    return (
      <div className="row g-3">
        <div className="col-lg-6"><div className="cv-bloco">
          <h6 className="fw-bold mb-2"><i className={`${P.iconeVenda} me-2`} style={{ color: P.corVenda }}></i>{P.rotuloVenda}</h6>
          <div className="row g-2">
            <Campo rotulo="Transação" mono>{v.txn || '—'}</Campo>
            <Campo rotulo="Cobrança" mono>{v.cobranca || '—'}</Campo>
            {P.eloCliente === 'userId' && <Campo rotulo="PaymentIntent" mono>{v.pi || '—'}</Campo>}
            <Campo rotulo={`Cliente ${P.nome}`} mono>{v.cus || '—'}</Campo>
            {P.eloCliente !== 'userId' && <Campo rotulo={`Nome no ${P.nome}`}>{nomeProvedor(v.cus)}</Campo>}
            {P.eloCliente === 'userId'
              ? <Campo rotulo="Código de integração" mono>{v.user_id || '—'}</Campo>
              : <Campo rotulo="De-para por">{v.user_id === 'documento' ? 'CPF/CNPJ' : v.user_id === 'email' ? 'E-mail' : '—'}</Campo>}
            <Campo rotulo="Meio">{meioVenda(v) || '—'}</Campo>
            <Campo rotulo="Bruto"><b>{brl(v.valor)}</b></Campo>
            <Campo rotulo="Tarifa">{brl(v.tarifa)}</Campo>
            <Campo rotulo="Líquido">{brl(v.liquido)}</Campo>
            <Campo rotulo="Criado">{dataHoraBr(v.criado)}</Campo>
            {P.temStatusPendente && <Campo rotulo="Disponível em">{dataBr(v.disponivel)}</Campo>}
            <Campo rotulo="Status">{badgeStatusStripe(v.status, P)}</Campo>
            {P.temStatusPendente && <Campo rotulo="Repasse" mono>{v.repasse || '—'}</Campo>}
            <Campo rotulo="Estornado">{Number(v.estornado) > 0 ? <span className="text-danger">{brl(v.estornado)}</span> : 'Não'}</Campo>
            <Campo rotulo="Motivo">{MOTIVOS[v.motivo]?.rotulo || v.motivo || '—'}</Campo>
          </div>
        </div></div>
        <div className="col-lg-6"><div className="cv-bloco">
          <h6 className="fw-bold mb-2"><i className="fas fa-file-invoice me-2" style={{ color: '#F1BE48' }}></i>Títulos deste cliente no Omie perto da venda</h6>
          {!tits.length ? (
            <span className="text-muted">
              {v.motivo === 'sem_depara'
                ? P.semDepara
                : 'Nenhum título deste cliente entre 20 dias antes e 15 depois da venda'}
            </span>
          ) : (
            <table className="table table-sm mb-0 bg-white">
              <thead><tr><th>OS</th><th>Emissão</th><th>Vencimento</th><th className="text-end">Valor</th><th>Status</th><th>Conta</th></tr></thead>
              <tbody>
                {tits.map((t) => (
                  <tr key={t.codigo}>
                    <td>{t.os || '—'}</td>
                    <td>{dataBr(t.emissao)}</td>
                    <td>{dataBr(t.vencimento)}</td>
                    <td className="text-end">{brl(t.valor)}</td>
                    <td>{t.status || '—'}</td>
                    <td>{nomeConta(t.conta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
      </div>
    );
  };

  // Detalhe da linha expandida: título no Omie × venda no Stripe (a venda escolhida, em "Várias vendas").
  // Bloco "Como cruzou": as três chaves do casamento (cliente, valor, data) com o resultado de cada uma.
  const comoCruzou = (l, v) => {
    if (!v) return null;
    const dias = (l.emissao && v.criado)
      ? Math.round((new Date(`${l.emissao}T00:00:00`) - new Date(`${v.criado.slice(0, 10)}T00:00:00`)) / 86400000) : null;
    const difValor = Math.abs(Number(l.valor) - Number(v.valor));
    const chaves = [
      P.eloCliente === 'userId'
        ? { ok: !!v.user_id && v.user_id === l.integracao, rotulo: 'Cliente',
          texto: <>Stripe <code>userId</code> <span className="font-monospace">{v.user_id || '—'}</span> = Omie <code>código de integração</code> <span className="font-monospace">{l.integracao || '—'}</span>, que pertence ao cliente Omie de código <b>{l.cliente}</b></> }
        : { ok: !!l.cliente, rotulo: 'Cliente',
          texto: <>Cliente {P.nome} <code>{v.cus}</code> casado com o cliente Omie cód. <b>{l.cliente}</b> por {v.user_id === 'documento' ? 'CPF/CNPJ' : 'e-mail'}</> },
      { ok: difValor < 0.005, rotulo: 'Valor',
        texto: <>{P.nome} {brl(v.valor)} = Omie {brl(l.valor)}{difValor >= 0.005 ? <span className="text-danger"> (diferença {brl(difValor)})</span> : ''}</> },
      { ok: dias != null && dias >= -1 && dias <= 12, rotulo: 'Data',
        texto: <>venda {dataBr(v.criado)} → emissão {dataBr(l.emissao)}{dias != null ? ` (${dias} dia${Math.abs(dias) === 1 ? '' : 's'}; aceito de −1 a +12)` : ''}</> },
    ];
    return (
      <div className="cv-bloco">
        <h6 className="fw-bold mb-2"><i className="fas fa-link me-2" style={{ color: '#00b5cc' }}></i>Como cruzou</h6>
        {chaves.map((c) => (
          <div key={c.rotulo} className="cv-cruzou">
            <i className={`fas ${c.ok ? 'fa-circle-check text-success' : 'fa-circle-xmark text-danger'}`}></i>
            <div><b>{c.rotulo}:</b> <small>{c.texto}</small></div>
          </div>
        ))}
      </div>
    );
  };

  const detalheLinha = (l, v) => (
    <div className="row g-3">
      {v && <div className="col-12">{comoCruzou(l, v)}</div>}
      <div className="col-lg-6"><div className="cv-bloco">
        <h6 className="fw-bold mb-2"><i className="fas fa-file-invoice me-2" style={{ color: '#F1BE48' }}></i>Título no Omie</h6>
        <div className="row g-2">
          <Campo rotulo="Cód. Omie" mono>{l.codigo}</Campo>
          <Campo rotulo="OS">{l.os || '—'}</Campo>
          <Campo rotulo="RPS">{l.documento || '—'}</Campo>
          <Campo rotulo="NF">{l.nf || '—'}</Campo>
          <Campo rotulo="Categoria">{l.categoria || '—'}</Campo>
          <Campo rotulo="Emissão">{dataBr(l.emissao)}</Campo>
          <Campo rotulo="Vencimento">{dataBr(l.vencimento)}</Campo>
          <Campo rotulo="Previsão">{dataBr(l.previsao)}</Campo>
          <Campo rotulo="Situação">{(() => {
            const st = String(l.status || '').toUpperCase();
            const baixadoAgora = !!resultados[l.codigo]?.ok;
            if (baixadoAgora || ['RECEBIDO', 'LIQUIDADO', 'PAGO'].includes(st)) {
              return <span className="badge border bg-success-subtle text-success-emphasis border-success-subtle"><i className="fas fa-circle-check me-1"></i>Quitado{baixadoAgora ? ' (agora)' : ''}</span>;
            }
            if (st === 'CANCELADO') {
              return <span className="badge border bg-secondary-subtle text-secondary-emphasis border-secondary-subtle">Cancelado</span>;
            }
            return <span className="badge border bg-warning-subtle text-warning-emphasis border-warning-subtle"><i className="fas fa-clock me-1"></i>Aberto · {l.status || '—'}</span>;
          })()}</Campo>
          <Campo rotulo="Valor"><b>{brl(l.valor)}</b></Campo>
          <div className="col-12 col-md-6">
            <small className="text-muted d-block">Cliente</small>
            <span>{l.nome || '—'} <small className="text-muted">· cód. {l.cliente ?? '—'}</small></span>
          </div>
        </div>
      </div></div>
      <div className="col-lg-6"><div className="cv-bloco">
        <h6 className="fw-bold mb-2"><i className={`${P.iconeVenda} me-2`} style={{ color: P.corVenda }}></i>{P.rotuloVenda}</h6>
        {!v ? <span className="text-muted">Sem venda casada</span> : (
          <div className="row g-2">
            <Campo rotulo="Transação" mono>{v.txn || '—'}</Campo>
            <Campo rotulo="Cobrança" mono>{v.cobranca || '—'}</Campo>
            {P.eloCliente === 'userId' && <Campo rotulo="PaymentIntent" mono>{v.pi || '—'}</Campo>}
            <Campo rotulo={`Cliente ${P.nome}`} mono>{v.cus || '—'}</Campo>
            {P.eloCliente !== 'userId' && <Campo rotulo={`Nome no ${P.nome}`}>{nomeProvedor(v.cus)}</Campo>}
            <Campo rotulo="Meio">{meioVenda(v) || '—'}</Campo>
            <Campo rotulo="Bruto"><b>{brl(v.valor)}</b></Campo>
            <Campo rotulo="Tarifa">{brl(v.tarifa)}</Campo>
            <Campo rotulo="Líquido">{brl(v.liquido)}</Campo>
            <Campo rotulo="Criado">{dataHoraBr(v.criado)}</Campo>
            {P.temStatusPendente && <Campo rotulo="Disponível em">{dataBr(v.disponivel)}</Campo>}
            <Campo rotulo="Status">{badgeStatusStripe(v.status, P)}</Campo>
            {P.temStatusPendente && <Campo rotulo="Repasse" mono>{v.repasse ? `${v.repasse}${v.repasse_chegada ? ` · chega ${dataBr(v.repasse_chegada)}` : ''}` : '—'}</Campo>}
            <Campo rotulo="Estornado">{Number(v.estornado) > 0 ? <span className="text-danger">{brl(v.estornado)}</span> : 'Não'}</Campo>
          </div>
        )}
      </div></div>
    </div>
  );

  const cardResumo = ({ chave, titulo, valor, sub, cor, icone, ativo, onClick }) => {
    const clicavel = !!onClick;
    return (
      <div className="col-6 col-md" key={chave}>
        <div className={`card h-100 cv-card${clicavel ? ' cv-card--clicavel' : ''}${ativo ? ' cv-card--ativo' : ''}`}
          role={clicavel ? 'button' : undefined} tabIndex={clicavel ? 0 : undefined}
          onClick={onClick}
          onKeyDown={clicavel ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
          style={{ cursor: clicavel ? 'pointer' : 'default', '--cv-cor': cor }}>
          <div className="card-body d-flex justify-content-between align-items-center gap-2">
            <div className="min-w-0">
              <span className="cv-rotulo">{titulo}</span>
              <h4 className="mb-0" style={{ color: cor }}>{valor}</h4>
              {sub && <span className="cv-sub">{sub}</span>}
            </div>
            <span className="cv-icone" aria-hidden="true"><i className={`fas ${icone}`}></i></span>
          </div>
        </div>
      </div>
    );
  };

  const linhasVazias = (n, vazio, temLinhas) => (temLinhas ? null : carregandoAba
    ? <tr><td colSpan={n} className="cv-vazio"><i className="fas fa-spinner fa-spin"></i>Carregando…</td></tr>
    : <tr><td colSpan={n} className="cv-vazio"><i className="fas fa-inbox"></i>{vazio}</td></tr>);

  const renderSobras = () => (
    <>
      <div className="row g-2 mb-2">
        {CARDS_SOBRAS.map((c) => cardResumo({
          chave: c.campo, titulo: c.titulo, valor: resumoSobras[c.campo] ?? 0,
          sub: c.valor ? brl(resumoSobras[c.valor] ?? 0) : null, cor: c.cor, icone: c.icone,
          ativo: c.chave !== null && filtroMotivo === c.chave && (c.chave !== '' || sobras.length > 0),
          onClick: c.chave === null ? undefined : () => setFiltroMotivo((f) => (f === c.chave ? '' : c.chave)),
        }))}
      </div>
      <p className="text-muted mb-3"><small><i className="fas fa-circle-info me-1"></i>{P.ajudaSobras}</small></p>
      <div className="mb-2">
        <small className="text-muted">{sobrasVisiveis.length} de {sobras.length} venda(s) sem título no Omie
          {filtroMotivo && <> · filtro: {MOTIVOS[filtroMotivo]?.rotulo || filtroMotivo} <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setFiltroMotivo('')}>limpar</button></>}
        </small>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle cv-table">
          <thead>
            <tr>
              <th>Data/hora</th><th>Cliente</th><th className="text-end">Valor</th><th className="text-end">Tarifa</th>
              <th className="text-end">Líquido</th><th>Meio</th><th>{P.temStatusPendente ? 'Status Stripe' : 'Status'}</th><th>Cobrança</th><th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {linhasVazias(9, P.vazioSobras, sobrasVisiveis.length > 0)}
            {sobrasVisiveis.map((v) => {
              const m = MOTIVOS[v.motivo] || { rotulo: v.motivo || '—', classe: 'bg-light border' };
              const aberto = !!expandidoSobra[v.txn];
              return (
                <React.Fragment key={v.txn}>
                <tr className={`cv-linha${aberto ? ' cv-linha--aberta' : ''}`} style={{ cursor: 'pointer' }} aria-expanded={aberto}
                  onClick={() => { buscarNomeProvedor(v.cus); setExpandidoSobra((p) => ({ ...p, [v.txn]: !p[v.txn] })); }}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <i className="fas fa-chevron-right cv-chevron me-2" aria-hidden="true"></i>
                    {dataHoraBr(v.criado)}
                  </td>
                  <td>
                    <div>{v.nome || <span className="text-muted">{v.cus || '—'}</span>}</div>
                    {v.cliente
                      ? <small className="text-muted">cód. {v.cliente}</small>
                      : <span className="badge border bg-secondary-subtle text-secondary-emphasis border-secondary-subtle">sem de-para</span>}
                  </td>
                  <td className="text-end fw-bold">{brl(v.valor)}</td>
                  <td className="text-end">{brl(v.tarifa)}</td>
                  <td className="text-end">{brl(v.liquido)}</td>
                  <td>{meioVenda(v) || '—'}</td>
                  <td>{badgeStatusStripe(v.status, P)}</td>
                  <td><small className="font-monospace">{v.cobranca || v.pi || v.txn}</small></td>
                  <td><span className={`badge border ${m.classe}`}>{m.rotulo}</span></td>
                </tr>
                {aberto && (
                  <tr className="cv-detalhe">
                    <td colSpan="9">{detalheSobra(v)}</td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );

  const statusRepasse = (r) => {
    const s = r.status_repasse || r.status;
    if (!s) return '—';
    return <span className={`badge border ${CLASSE_STATUS_REPASSE[s] || 'bg-light text-dark'}`}>{STATUS_REPASSE[s] || s}</span>;
  };
  const omieRepasse = (r) => (r.omie
    ? <span className="badge border bg-success-subtle text-success-emphasis border-success-subtle" title={r.omie.obs || undefined}>Transferência cód. {r.omie.codigo} em {dataBr(r.omie.data)}</span>
    : <span className="badge border bg-danger-subtle text-danger-emphasis border-danger-subtle">Sem transferência</span>);

  // Asaas: saques simples (sem vendas/tarifas por saque e sem lançamento no Omie).
  const renderSaques = () => (
    <>
      <div className="row g-2 mb-2">
        {CARDS_SAQUES.map((c) => cardResumo({
          chave: c.campo, titulo: c.titulo, cor: c.cor, icone: c.icone,
          valor: c.moeda ? brl(resumoRep[c.campo] ?? 0) : (resumoRep[c.campo] ?? 0),
        }))}
      </div>
      <p className="text-muted mb-3"><small><i className="fas fa-circle-info me-1"></i>{P.ajudaRepasses}</small></p>
      <div className="table-responsive">
        <table className="table table-sm align-middle cv-table">
          <thead>
            <tr><th>Data</th><th className="text-end">Valor</th><th>Destino</th><th>Status</th><th>Omie</th></tr>
          </thead>
          <tbody>
            {linhasVazias(5, P.vazioRepasses, repasses.length > 0)}
            {repasses.map((r) => (
              <tr key={r.po || r.txn} className="cv-linha">
                <td style={{ whiteSpace: 'nowrap' }}>{dataHoraBr(r.criado)}{r.po && <><br /><small className="text-muted font-monospace">{r.po}</small></>}</td>
                <td className="text-end fw-bold">{brl(r.valor)}</td>
                <td><small>{r.banco || '—'}</small></td>
                <td>{statusRepasse(r)}</td>
                <td>{omieRepasse(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {outros.length > 0 && (() => {
        const semOmie = outros.filter((o) => !o.omie);
        const visiveis = filtroOutros === 'sem_omie' ? semOmie : outros;
        const totalSem = semOmie.reduce((s, o) => s + Number(o.valor || 0), 0);
        return (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h6 className="fw-bold m-0"><i className="fas fa-receipt me-2" style={{ color: '#d4af37' }}></i>
                Outros lançamentos do {P.nome} × extrato da conta {P.conta} no Omie</h6>
              <div className="d-flex align-items-center gap-2">
                <span className="badge border bg-danger-subtle text-danger-emphasis border-danger-subtle">
                  Sem lançamento no Omie: {semOmie.length} grupo(s) · {brl(totalSem)}</span>
                <select className="form-select form-select-sm" style={{ width: 'auto' }} value={filtroOutros} onChange={(e) => setFiltroOutros(e.target.value)}>
                  <option value="sem_omie">Só sem lançamento no Omie</option>
                  <option value="todos">Todos os grupos</option>
                </select>
              </div>
            </div>
            <p className="text-muted mb-2"><small><i className="fas fa-circle-info me-1"></i>
              Taxas, comissões de subconta, estornos e transferências internas, agrupados por dia e tipo. Cada grupo é procurado no extrato da conta {P.conta} do Omie pelo valor exato (±3 dias). Tudo que entra ou sai do {P.nome} precisa ter contrapartida no Omie.</small></p>
            <div className="table-responsive">
              <table className="table table-sm align-middle cv-table">
                <thead><tr><th>Data</th><th>Tipo</th><th className="text-end">Lançamentos</th><th className="text-end">Valor</th><th>Omie</th></tr></thead>
                <tbody>
                  {!visiveis.length && <tr><td colSpan="5" className="cv-vazio"><i className="fas fa-circle-check"></i>Tudo conferido no período.</td></tr>}
                  {visiveis.map((o) => (
                    <tr key={`${o.data}-${o.tipo}`} className="cv-linha">
                      <td style={{ whiteSpace: 'nowrap' }}>{dataBr(o.data)}</td>
                      <td>{o.rotulo}<br /><small className="text-muted font-monospace">{o.tipo}</small></td>
                      <td className="text-end">{o.qtd}</td>
                      <td className={`text-end fw-bold ${Number(o.valor) < 0 ? 'text-danger' : 'text-success'}`}>{brl(o.valor)}</td>
                      <td>{o.omie
                        ? <span className="badge border bg-success-subtle text-success-emphasis border-success-subtle" title={o.omie.obs || undefined}>Lançamento {o.omie.codigo} em {dataBr(o.omie.data)}</span>
                        : <span className="badge border bg-danger-subtle text-danger-emphasis border-danger-subtle">Sem lançamento no Omie</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </>
  );

  // ── Aba Extrato ──
  // Tarifa irmã da venda no Stripe: linha `${id}:tarifa` (valor negativo) → tarifa positiva.
  const tarifaIrma = (l) => {
    const irma = (extrato?.linhas || []).find((x) => x.id === `${l.id}:tarifa`);
    return irma ? -Number(irma.valor || 0) : 0;
  };
  const vendaDaLinhaExtrato = (l) => {
    const tarifa = tarifaIrma(l);
    return {
      txn: l.id, cobranca: l.cobranca, pi: l.pi, cus: l.cus, valor: l.valor, tarifa, liquido: Number(l.valor || 0) - tarifa,
      criado: l.hora || l.data, status: l.status || 'available', metodo: P.eloCliente === 'userId' ? null : 'PIX',
      bandeira: null, parcelas: null, repasse: l.repasse, estornado: 0,
    };
  };
  const alternarFiltroExtrato = (f) => setFiltroExtrato((atual) => (atual === f ? 'todos' : f));

  const renderExtrato = () => {
    const rs = extrato?.resumo || {};
    const total = extrato?.linhas?.length || 0;
    const mostradas = linhasExtratoFiltradas.slice(0, LIMITE_EXTRATO);
    const semGateway = extrato?.omie_sem_gateway || [];
    const saldoPartes = [];
    if (rs.saldo_gateway != null) saldoPartes.push(`Saldo ${P.nome} em ${dataBr(extrato?.ate || periodoExtrato.ate)}: ${brl(rs.saldo_gateway)}`);
    if (rs.saldo_omie != null) saldoPartes.push(`Saldo atual da conta ${P.conta} no Omie: ${brl(rs.saldo_omie)}`);
    if (rs.saldo_gateway != null && rs.saldo_omie != null) saldoPartes.push(`diferença ${brl(Number(rs.saldo_omie) - Number(rs.saldo_gateway))}`);
    const vazio = (n, texto) => (carregandoExtrato
      ? <tr><td colSpan={n} className="cv-vazio"><i className="fas fa-spinner fa-spin"></i>Carregando…</td></tr>
      : <tr><td colSpan={n} className="cv-vazio"><i className="fas fa-inbox"></i>{texto}</td></tr>);
    const semPeriodo = 'Escolha o período e clique em Atualizar.';
    return (
      <>
        <div className="row g-2 mb-2">
          {cardResumo({ chave: 'ext_linhas', titulo: 'Linhas', valor: rs.linhas ?? 0, cor: '#00205b', icone: 'fa-list-ul',
            ativo: filtroExtrato === 'todos' && total > 0, onClick: () => setFiltroExtrato('todos') })}
          {cardResumo({ chave: 'ext_entradas', titulo: 'Entradas', valor: brl(rs.entradas ?? 0), cor: '#198754', icone: 'fa-arrow-down' })}
          {cardResumo({ chave: 'ext_saidas', titulo: 'Saídas', valor: brl(rs.saidas ?? 0), cor: '#dc3545', icone: 'fa-arrow-up' })}
          {cardResumo({ chave: 'ext_vendas', titulo: 'Vendas conciliadas', valor: `${rs.vendas_conciliadas ?? 0}/${rs.vendas ?? 0}`, cor: '#00b5cc', icone: 'fa-cart-shopping',
            ativo: filtroExtrato === 'conciliados', onClick: () => alternarFiltroExtrato('conciliados') })}
          {cardResumo({ chave: 'ext_nao', titulo: 'Não conciliados', valor: rs.nao_conciliados ?? 0, sub: brl(rs.nao_conciliados_valor ?? 0), cor: '#dc3545', icone: 'fa-link-slash',
            ativo: filtroExtrato === 'nao', onClick: () => alternarFiltroExtrato('nao') })}
          {cardResumo({ chave: 'ext_baixaveis', titulo: 'Prontos para conciliar', valor: rs.baixaveis ?? 0, cor: '#198754', icone: 'fa-circle-check',
            ativo: filtroExtrato === 'baixaveis', onClick: () => alternarFiltroExtrato('baixaveis') })}
          {cardResumo({ chave: 'ext_sem_titulo', titulo: 'Sem título', valor: rs.sem_titulo ?? 0, cor: '#6c757d', icone: 'fa-file-circle-xmark' })}
          {cardResumo({ chave: 'ext_omie_sem', titulo: `No Omie sem ${P.nome}`, valor: rs.omie_sem_gateway ?? 0, sub: brl(rs.omie_sem_gateway_valor ?? 0), cor: '#b8860b', icone: 'fa-file-invoice' })}
        </div>
        {saldoPartes.length > 0 && (
          <p className="mb-1"><small><i className="fas fa-scale-balanced me-1" style={{ color: '#00b5cc' }}></i>{saldoPartes.join(' · ')}</small></p>
        )}
        <p className="text-muted mb-3"><small><i className="fas fa-circle-info me-1"></i>
          Vendas são conciliadas pelo título do Omie (cliente + valor + emissão até 12 dias). Tarifas, repasses, estornos e comissões são conciliados pelo valor no extrato da conta {P.conta} (±3 dias). Nada é lançado no Omie sem clicar em Conciliar.</small></p>

        {(rs.baixaveis ?? 0) > 0 && filtroExtrato !== 'baixaveis' && (
          <div className="alert alert-success d-flex align-items-center justify-content-between py-2 mb-3" role="alert">
            <span><i className="fas fa-circle-check me-2"></i>
              <strong>{rs.baixaveis}</strong> {rs.baixaveis === 1 ? 'venda tem' : 'vendas têm'} título em aberto no Omie e {rs.baixaveis === 1 ? 'pode' : 'podem'} ser conciliada{rs.baixaveis === 1 ? '' : 's'} agora.
              O extrato é cronológico: o botão <strong>Conciliar</strong> aparece só nessas linhas.
            </span>
            <button className="btn btn-sm btn-success ms-3 text-nowrap" onClick={() => setFiltroExtrato('baixaveis')}>
              <i className="fas fa-filter me-1"></i>Ver só as prontas
            </button>
          </div>
        )}

        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <select className="form-select" value={filtroExtrato} aria-label="Filtro do extrato" onChange={(e) => setFiltroExtrato(e.target.value)}>
              {FILTROS_EXTRATO.map((f) => <option key={f.chave} value={f.chave}>{f.rotulo}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <input className="form-control" placeholder="Descrição, cliente, cobrança ou tipo" value={buscaExtrato} onChange={(e) => setBuscaExtrato(e.target.value)} />
          </div>
          <div className="col-md-5 d-flex align-items-center">
            <small className="text-muted">{linhasExtratoFiltradas.length} de {total} linhas</small>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle cv-table">
            <thead>
              <tr>
                <th>Data</th><th>Tipo</th><th>Descrição</th><th className="text-end">Entrada</th><th className="text-end">Saída</th>
                <th>Omie</th><th className="text-end">Ação</th>
              </tr>
            </thead>
            <tbody>
              {!mostradas.length && vazio(7, extrato ? 'Nenhuma linha neste filtro.' : semPeriodo)}
              {mostradas.map((l, i) => {
                const v = Number(l.valor || 0);
                const res = l.titulo ? resultados[l.titulo.codigo] : null;
                return (
                  <tr key={l.id || i} className="cv-linha">
                    <td style={{ whiteSpace: 'nowrap' }}>{dataBr(l.data)}{l.hora && l.hora.length > 10 && <><br /><small className="text-muted">{l.hora.slice(11, 16)}</small></>}</td>
                    <td>{l.rotulo || l.tipo}{l.tipo_bruto && <><br /><small className="text-muted font-monospace">{l.tipo_bruto}</small></>}</td>
                    <td>
                      {l.nome
                        ? <><div>{l.nome}</div>{l.cobranca && <small className="text-muted font-monospace">{l.cobranca}</small>}</>
                        : <span>{l.descricao || '—'}</span>}
                    </td>
                    <td className="text-end text-success fw-bold">{v > 0 ? brl(v) : ''}</td>
                    <td className="text-end text-danger fw-bold">{v < 0 ? brl(v) : ''}</td>
                    <td>{l.omie
                      ? <span className="badge border bg-success-subtle text-success-emphasis border-success-subtle" title={l.omie.obs || undefined}>
                        Conciliado · {l.omie.origem === 'Título recebido' ? `título OS ${l.omie.os}` : `lanç. ${l.omie.codigo}`} em {dataBr(l.omie.data)}</span>
                      : <span className="badge border bg-danger-subtle text-danger-emphasis border-danger-subtle">Não conciliado</span>}</td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      {l.acao === 'baixar' && l.titulo
                        ? (res?.ok
                          ? <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle"><i className="fas fa-check me-1"></i>{res.ja_baixado ? 'Já baixado no Omie' : `Baixado · ${res.codigo_baixa}`}</span>
                          : <button className="btn btn-sm btn-primary" disabled={ocupado} title={res?.erro || 'Abre a confirmação da baixa deste título no Omie'}
                            onClick={() => setModal({ tipo: 'um', linha: { ...l.titulo, venda: vendaDaLinhaExtrato(l) } })}>
                            <i className="fas fa-check me-1"></i>{res?.erro ? 'Tentar de novo' : 'Conciliar'}
                          </button>)
                        : l.acao === 'sem_titulo'
                          ? <small className="text-muted">Sem título no Omie</small>
                          : l.tipo === 'venda' && l.omie
                            ? <small className="text-muted"><i className="fas fa-check me-1"></i>Já recebido</small>
                            : <span className="text-muted" title="Linha sem baixa a fazer (não é venda)">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {linhasExtratoFiltradas.length > LIMITE_EXTRATO && (
          <p className="text-muted"><small>mostrando {LIMITE_EXTRATO} de {linhasExtratoFiltradas.length} — refine o período ou o filtro</small></p>
        )}

        <div className="mt-4">
          <h6 className="fw-bold mb-2"><i className="fas fa-file-invoice me-2" style={{ color: '#d4af37' }}></i>No Omie sem correspondência no {P.nome}</h6>
          <div className="table-responsive">
            <table className="table table-sm align-middle cv-table">
              <thead><tr><th>Data</th><th>Origem</th><th>Categoria</th><th>Documento / Obs</th><th className="text-end">Valor</th><th>Motivo</th></tr></thead>
              <tbody>
                {!semGateway.length && vazio(6, extrato ? 'Nenhum lançamento do Omie sem correspondência neste período.' : semPeriodo)}
                {semGateway.map((o, i) => {
                  const m = MOTIVOS_OMIE_SEM_GATEWAY[o.motivo];
                  const v = Number(o.valor || 0);
                  return (
                    <tr key={`${o.codigo}-${i}`} className="cv-linha">
                      <td style={{ whiteSpace: 'nowrap' }}>{dataBr(o.data)}</td>
                      <td>{o.origem || '—'}{o.situacao && <><br /><small className="text-muted">{o.situacao}</small></>}</td>
                      <td>{o.categoria || '—'}{o.categoria_nome && <><br /><small className="text-muted">{o.categoria_nome}</small></>}</td>
                      <td>{o.documento || '—'}{o.obs && <><br /><small className="text-muted">{o.obs}</small></>}</td>
                      <td className={`text-end fw-bold ${v < 0 ? 'text-danger' : 'text-success'}`}>{brl(v)}</td>
                      <td><span className={`badge border ${m ? m.classe : 'bg-light'}`}>{m ? m.rotulo(P.nome) : (o.motivo || '—')}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderRepasses = () => (!P.temRepasseComposto ? renderSaques() : (
    <>
      <div className="row g-2 mb-2">
        {CARDS_REP.map((c) => cardResumo({
          chave: c.campo, titulo: c.titulo, cor: c.cor, icone: c.icone,
          valor: c.moeda ? brl(resumoRep[c.campo] ?? 0) : (resumoRep[c.campo] ?? 0),
        }))}
      </div>
      <p className="text-muted mb-3"><small><i className="fas fa-circle-info me-1"></i>Sem transferência = o repasse chegou no banco, mas não há saída correspondente na conta Stripe do Omie.</small></p>
      <div className="table-responsive">
        <table className="table table-sm align-middle cv-table">
          <thead>
            <tr>
              <th>Criado</th><th>Chegada</th><th className="text-end">Valor</th><th>Banco destino</th>
              <th className="text-end">Vendas</th><th className="text-end">Bruto</th><th className="text-end">Tarifas</th><th>Status</th><th>Omie</th>
              <th className="text-end">Ação</th>
            </tr>
          </thead>
          <tbody>
            {linhasVazias(10, 'Nenhum repasse neste período.', repasses.length > 0)}
            {repasses.map((r) => (
              <tr key={r.po || r.txn} className="cv-linha">
                <td style={{ whiteSpace: 'nowrap' }}>{dataHoraBr(r.criado)}{r.po && <><br /><small className="text-muted font-monospace">{r.po}</small></>}</td>
                <td>{dataBr(r.chegada)}</td>
                <td className="text-end fw-bold">{brl(r.valor)}</td>
                <td>{r.banco ? `${r.banco} · ag ${r.agencia || '—'} · conta ··${r.conta_final || '—'}` : '—'}</td>
                <td className="text-end">{r.vendas ?? 0}</td>
                <td className="text-end">{brl(r.bruto)}</td>
                <td className="text-end">{brl(r.tarifas)}</td>
                <td>{(() => {
                  const s = r.status_repasse || r.status;
                  if (!s) return '—';
                  return <span className={`badge border ${CLASSE_STATUS_REPASSE[s] || 'bg-light text-dark'}`}>{STATUS_REPASSE[s] || s}</span>;
                })()}</td>
                <td>
                  {r.omie
                    ? <span className="badge border bg-success-subtle text-success-emphasis border-success-subtle" title={r.omie.obs || undefined}>Transferência cód. {r.omie.codigo} em {dataBr(r.omie.data)}</span>
                    : <span className="badge border bg-danger-subtle text-danger-emphasis border-danger-subtle">Sem transferência</span>}
                </td>
                <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{acaoRepasse(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  ));

  return (
    <div className="painel-op cv-page"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className={`fas ${P.icone} header-icon me-2`} aria-hidden="true"></i>
          <div>
            <h2 className="m-0">{P.titulo}</h2>
            <small className="text-muted">
              {aba === 'em_aberto' ? P.subtitulos.em_aberto
                : aba === 'stripe_sem_omie' ? P.subtitulos.sobras
                  : aba === 'extrato' ? P.subtitulos.extrato
                    : P.subtitulos.repasses}
              {aba === 'em_aberto'
                ? (atualizadoEm ? ` · atualizado ${dataHoraBr(atualizadoEm)}` : '')
                : (atualizadoAba ? ` · atualizado ${dataHoraBr(atualizadoAba)}` : '')}
            </small>
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {aba === 'em_aberto' ? (
            <>
              <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={carregar} disabled={carregando || ocupado}>
                <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
              </button>
              <button className="btn btn-dark d-flex align-items-center gap-2" disabled={!casados.length || ocupado || carregando}
                onClick={() => setModal({ tipo: 'lote' })}>
                <i className="fas fa-layer-group" aria-hidden="true"></i>Baixar todos os casados ({casados.length})
              </button>
            </>
          ) : aba === 'extrato' ? (
            <>
              <label className="d-flex align-items-center gap-1 mb-0"><small className="text-muted">De</small>
                <input type="date" className="form-control" style={{ width: 'auto' }} value={periodoExtrato.de} disabled={carregandoExtrato}
                  onChange={(e) => setPeriodoExtrato((p) => ({ ...p, de: e.target.value }))} />
              </label>
              <label className="d-flex align-items-center gap-1 mb-0"><small className="text-muted">Até</small>
                <input type="date" className="form-control" style={{ width: 'auto' }} value={periodoExtrato.ate} disabled={carregandoExtrato}
                  onChange={(e) => setPeriodoExtrato((p) => ({ ...p, ate: e.target.value }))} />
              </label>
              <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={() => carregarExtrato()} disabled={carregandoExtrato || ocupado}>
                <i className={`fas ${carregandoExtrato ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
              </button>
            </>
          ) : (
            <>
              <select className="form-select" style={{ width: 'auto' }} value={dias} disabled={carregandoAba || ocupadoRep}
                aria-label="Período" onChange={(e) => trocarDias(Number(e.target.value))}>
                {PERIODOS.map((p) => <option key={p} value={p}>Últimos {p} dias</option>)}
              </select>
              <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={() => carregarAba(aba, dias)} disabled={carregandoAba || ocupadoRep}>
                <i className={`fas ${carregandoAba ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
              </button>
              {aba === 'repasses' && P.temLancarRepasse && (
                <button className="btn btn-dark d-flex align-items-center gap-2" disabled={!pendentesRep.length || ocupadoRep || carregandoAba}
                  onClick={() => setModalRep({ tipo: 'lote' })}>
                  <i className="fas fa-layer-group" aria-hidden="true"></i>Lançar todos sem transferência ({pendentesRep.length})
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <ul className="nav nav-tabs cv-tabs mb-3">
        {ABAS.map((a) => (
          <li className="nav-item" key={a.chave}>
            <button type="button" className={`nav-link ${aba === a.chave ? 'active' : ''}`} disabled={(ocupado || ocupadoRep) && aba !== a.chave}
              onClick={() => trocarAba(a.chave)}>
              <i className={`fas ${a.icone} me-2`} aria-hidden="true"></i>{P.abas[a.rotuloDe]}
            </button>
          </li>
        ))}
      </ul>

      {aba === 'em_aberto' && (<>

      <div className="row g-2 mb-3">
        {CARDS.map((c) => cardResumo({
          chave: c.campo, titulo: c.titulo, valor: resumo[c.campo] ?? 0, cor: c.cor, icone: c.icone,
          ativo: !!c.chave && filtro === c.chave,
          onClick: c.chave === 'baixados' ? undefined : () => setFiltro((f) => (f === c.chave ? '' : c.chave)),
        }))}
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select className="form-select" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="">Todas as situações</option>
            {Object.entries(SITUACOES).map(([k, s]) => <option key={k} value={k}>{s.rotulo}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <input className="form-control" placeholder={P.buscaPlaceholder} value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="col-md-5 d-flex align-items-center">
          <small className="text-muted">{visiveis.length} de {titulos.length} título(s){casados.length ? ` · casados a baixar: ${brl(totalCasados)}` : ''}</small>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-sm align-middle cv-table">
          <thead>
            <tr>
              <th>Cliente</th><th>OS / RPS</th><th>Emissão</th><th>Venc.</th><th className="text-end">Valor</th>
              <th>Situação</th><th>{P.rotuloVenda}</th><th className="text-end">Ação</th>
            </tr>
          </thead>
          <tbody>
            {carregando && !titulos.length && <tr><td colSpan="8" className="cv-vazio"><i className="fas fa-spinner fa-spin"></i>Carregando…</td></tr>}
            {!carregando && !visiveis.length && <tr><td colSpan="8" className="cv-vazio"><i className="fas fa-inbox"></i>Nenhum título em aberto nesta situação.</td></tr>}
            {visiveis.map((l) => {
              const s = SITUACOES[l.situacao] || { rotulo: l.situacao, classe: 'bg-light border', icone: 'fa-question' };
              const r = resultados[l.codigo];
              const venda = vendaDaLinha(l);
              const aberto = !!expandido[l.codigo];
              return (
                <React.Fragment key={l.codigo}>
                <tr className={`cv-linha${aberto ? ' cv-linha--aberta' : ''}${r?.ok ? ' cv-linha--baixada' : ''}`} style={{ cursor: 'pointer' }}
                  onClick={() => { buscarNomeProvedor(venda?.cus); alternar(l.codigo); }} aria-expanded={aberto}>
                  <td>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-chevron-right cv-chevron me-2 mt-1" aria-hidden="true"></i>
                      <div><div>{l.nome || <span className="text-muted">cliente {l.cliente}</span>}</div><small className="text-muted">cód. {l.cliente}</small></div>
                    </div>
                  </td>
                  <td><div>OS {l.os || '-'}</div><small className="text-muted">{l.documento || ''}{l.nf ? ` · NF ${l.nf}` : ''}</small></td>
                  <td>{dataBr(l.emissao)}</td>
                  <td>{dataBr(l.vencimento)}</td>
                  <td className="text-end fw-bold">{brl(l.valor)}</td>
                  <td>
                    <span className={`badge border ${s.classe}`}><i className={`fas ${s.icone} me-1`}></i>{s.rotulo}</span>
                    {l.situacao === 'varias' && !r?.ok && (
                      <select className="form-select form-select-sm mt-1" value={escolha[l.codigo] || l.venda?.txn || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setEscolha((p) => ({ ...p, [l.codigo]: e.target.value }))}>
                        {l.candidatas.map((c) => <option key={c.txn} value={c.txn}>{dataHoraBr(c.criado)} · {c.cobranca || c.txn}</option>)}
                      </select>
                    )}
                  </td>
                  <td><VendaStripe v={venda} P={P} /></td>
                  <td className="text-end" style={{ whiteSpace: 'nowrap', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                    {r?.ok
                      ? <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle"><i className="fas fa-check me-1"></i>{r.ja_baixado ? 'Já baixado no Omie' : `Baixado · ${r.codigo_baixa}`}</span>
                      : r?.erro
                        ? <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle" title={r.erro}><i className="fas fa-triangle-exclamation me-1"></i>Erro</span>
                        : null}
                    {!r?.ok && (l.situacao === 'casado' || l.situacao === 'varias') && (
                      <button className="btn btn-sm btn-outline-primary ms-1" disabled={ocupado} onClick={() => setModal({ tipo: 'um', linha: l })}>
                        <i className="fas fa-check me-1"></i>{r?.erro ? 'Tentar de novo' : 'Baixar no Omie'}
                      </button>
                    )}
                  </td>
                </tr>
                {aberto && (
                  <tr className="cv-detalhe">
                    <td colSpan="8">{detalheLinha(l, venda)}</td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      </>)}

      {aba === 'stripe_sem_omie' && renderSobras()}
      {aba === 'repasses' && renderRepasses()}
      {aba === 'extrato' && renderExtrato()}

      {modal?.tipo === 'um' && (() => {
        const l = modal.linha; const venda = vendaDaLinha(l);
        return (
          <Modal titulo={`Baixar OS ${l.os} no Omie`} onFechar={() => setModal(null)} ocupado={ocupado}
            rodape={<>
              <button className="btn btn-outline-secondary" onClick={() => setModal(null)} disabled={ocupado}>Cancelar</button>
              <button className="btn btn-dark" onClick={() => baixarUm(l)} disabled={ocupado}>
                <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>Baixar no Omie
              </button>
            </>}>
            <div className="row g-3">
              <div className="col-md-6">
                <h6 className="fw-bold"><i className="fas fa-file-invoice me-2" style={{ color: '#F1BE48' }}></i>Título no Omie</h6>
                <div>{l.nome}</div>
                <div>OS {l.os} · {l.documento}{l.nf ? ` · NF ${l.nf}` : ''}</div>
                <div>Emissão {dataBr(l.emissao)} · venc. {dataBr(l.vencimento)}</div>
                <div className="fw-bold">{brl(l.valor)}</div>
              </div>
              <div className="col-md-6">
                <h6 className="fw-bold"><i className={`${P.iconeVenda} me-2`} style={{ color: P.corVenda }}></i>{P.rotuloVenda}</h6>
                <VendaStripe v={venda} P={P} />
              </div>
            </div>
            <div className="alert alert-light border mt-3 mb-0 py-2">
              <small>A baixa será lançada na conta <b>{P.conta}</b> do Omie pelo valor <b>{brl(l.valor)}</b> {P.textoBaixa} (<b>{dataBr(venda?.criado)}</b>), sem desconto ou juros, com a cobrança do {P.nome} na observação.</small>
            </div>
          </Modal>
        );
      })()}

      {modal?.tipo === 'lote' && (
        <Modal titulo={lote ? `Baixando em lote — ${lote.feitos} de ${lote.total}` : `Baixar ${casados.length} título(s) casado(s)`}
          onFechar={lote && !ocupado ? fecharLote : () => setModal(null)} ocupado={ocupado}
          rodape={lote
            ? <button className="btn btn-dark" onClick={fecharLote} disabled={ocupado}>Fechar e atualizar</button>
            : <>
              <button className="btn btn-outline-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-dark" onClick={baixarLote}><i className="fas fa-layer-group me-2"></i>Baixar os {casados.length} no Omie</button>
            </>}>
          {!lote ? (
            <>
              <p>Serão baixados no Omie <b>{casados.length}</b> títulos casados, somando <b>{brl(totalCasados)}</b>, cada um pelo valor do título, {P.textoBaixaLote}.</p>
              <p className="mb-0 text-muted"><small>O processamento é em blocos de {LOTE}; cada linha recebe o resultado do Omie. Títulos "Várias vendas", "Estornada" e "Sem venda" ficam de fora.</small></p>
            </>
          ) : (
            <>
              <div className="progress mb-3" style={{ height: 10 }}>
                <div className="progress-bar" role="progressbar" style={{ width: `${lote.total ? (100 * lote.feitos) / lote.total : 0}%`, background: '#00b5cc' }}></div>
              </div>
              <div className="d-flex gap-3 flex-wrap mb-2">
                <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">Baixados: {lote.ok}</span>
                <span className="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle">Já estavam baixados: {lote.jaBaixados}</span>
                <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle">Erros: {lote.erros.length}</span>
              </div>
              {ocupado && <p className="text-muted mb-2"><i className="fas fa-spinner fa-spin me-2"></i>Chamando o Omie, uma baixa por vez…</p>}
              {!!lote.erros.length && (
                <div className="table-responsive" style={{ maxHeight: 260 }}>
                  <table className="table table-sm mb-0">
                    <thead><tr><th>OS</th><th>Cliente</th><th>Erro</th></tr></thead>
                    <tbody>{lote.erros.map((e, i) => <tr key={i}><td>{e.os}</td><td>{e.nome}</td><td><small className="text-danger">{e.erro}</small></td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal>
      )}

      {modalRep?.tipo === 'um' && (() => {
        const r = modalRep.r;
        const tarifa = Number(r.tarifas || 0);
        const parcial = r.lancamento === 'parcial' || (resultadosRep[r.po] && !resultadosRep[r.po].ok && resultadosRep[r.po].lanc_transf != null);
        return (
          <Modal titulo={`Lançar repasse ${r.po} no Omie`} onFechar={() => setModalRep(null)} ocupado={ocupadoRep}
            rodape={<>
              <button className="btn btn-outline-secondary" onClick={() => setModalRep(null)} disabled={ocupadoRep}>Cancelar</button>
              <button className="btn btn-dark" onClick={() => lancarRepasse(r)} disabled={ocupadoRep}>
                <i className={`fas ${ocupadoRep ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>{parcial ? 'Completar no Omie' : 'Lançar no Omie'}
              </button>
            </>}>
            <div className="row g-3">
              <div className="col-md-6">
                <h6 className="fw-bold"><i className="fas fa-money-bill-transfer me-2" style={{ color: '#F1BE48' }}></i>Transferência</h6>
                {parcial
                  ? <div className="text-success"><i className="fas fa-check me-1"></i>Já lançada (cód. {(resultadosRep[r.po]?.lanc_transf ?? r.lanc_transf_omie) || 'já existia'})</div>
                  : <>
                    <div>Stripe → Banco Inter</div>
                    <div className="fw-bold">{brl(r.valor)}</div>
                    <div>Data {dataBr(r.chegada || r.criado)}</div>
                    <small className="text-muted">Categoria 0.01.02 Saída de Transferência</small>
                  </>}
              </div>
              <div className="col-md-6">
                <h6 className="fw-bold"><i className="fas fa-percent me-2" style={{ color: '#F1BE48' }}></i>Tarifa</h6>
                {tarifa > 0 ? (
                  <>
                    <div className="fw-bold">{brl(r.tarifas)}</div>
                    <div>Na conta Stripe</div>
                    <small className="text-muted">Categoria 2.05.04 Tarifas Bancárias</small>
                  </>
                ) : <span className="text-muted">Sem tarifa neste repasse</span>}
              </div>
            </div>
            <div className="alert alert-light border mt-3 mb-0 py-2">
              <small>
                Destino no Stripe: <b>{r.banco ? `${r.banco} · ag ${r.agencia || '—'} · conta ··${r.conta_final || '—'}` : '—'}</b> (confira se é o Banco Inter).
                <br />Se o Omie já tiver a transferência deste repasse, nada é criado.
              </small>
            </div>
          </Modal>
        );
      })()}

      {modalRep?.tipo === 'lote' && (
        <Modal titulo={loteRep ? `Lançando repasses — ${loteRep.feitos} de ${loteRep.total}` : `Lançar ${pendentesRep.length} repasse(s) sem transferência`}
          onFechar={loteRep && !ocupadoRep ? fecharLoteRep : () => setModalRep(null)} ocupado={ocupadoRep}
          rodape={loteRep
            ? <button className="btn btn-dark" onClick={fecharLoteRep} disabled={ocupadoRep}>Fechar e atualizar</button>
            : <>
              <button className="btn btn-outline-secondary" onClick={() => setModalRep(null)}>Cancelar</button>
              <button className="btn btn-dark" onClick={lancarRepassesLote} disabled={!pendentesRep.length}>
                <i className="fas fa-layer-group me-2"></i>Lançar os {pendentesRep.length} no Omie
              </button>
            </>}>
          {!loteRep ? (
            <>
              <p>Serão lançados no Omie <b>{pendentesRep.length}</b> repasse(s), somando <b>{brl(totalPendRep)}</b> em transferências (Stripe → Banco Inter, categoria 0.01.02) e <b>{brl(tarifasPendRep)}</b> em tarifas (conta Stripe, categoria 2.05.04).</p>
              <p className="mb-0 text-muted"><small>O processamento é em blocos de {LOTE_REP}; cada linha recebe o resultado do Omie. Repasses com transferência já existente no Omie não geram lançamento.</small></p>
            </>
          ) : (
            <>
              <div className="progress mb-3" style={{ height: 10 }}>
                <div className="progress-bar" role="progressbar" style={{ width: `${loteRep.total ? (100 * loteRep.feitos) / loteRep.total : 0}%`, background: '#00b5cc' }}></div>
              </div>
              <div className="d-flex gap-3 flex-wrap mb-2">
                <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">Lançados: {loteRep.ok}</span>
                <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle">Erros: {loteRep.erros.length}</span>
              </div>
              {ocupadoRep && <p className="text-muted mb-2"><i className="fas fa-spinner fa-spin me-2"></i>Chamando o Omie, um repasse por vez…</p>}
              {!!loteRep.erros.length && (
                <div className="table-responsive" style={{ maxHeight: 260 }}>
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Repasse</th><th className="text-end">Valor</th><th>Erro</th></tr></thead>
                    <tbody>{loteRep.erros.map((e, i) => <tr key={i}><td><small className="font-monospace">{e.po}</small></td><td className="text-end">{brl(e.valor)}</td><td><small className="text-danger">{e.erro}</small></td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal>
      )}
    </div></div>
  );
}
